import { fetchListData } from "components/list/useListQuery";

function stateManagerKey(endpoint, version) {
  return `react-list--${endpoint}--${version}`;
}

const baseStateManager = {
    init(context) {
      const { endpoint, version } = context;
      const latestKey = stateManagerKey(endpoint, version);
      const storageKeyBase = String(version || "").replace(/\d+$/, "");
      const targetPrefix = storageKeyBase
        ? `react-list--${endpoint}--${storageKeyBase}`
        : `react-list--${endpoint}--`;

      const staleKeys = Object.keys(localStorage).filter(
        (key) => key.startsWith(targetPrefix) && key !== latestKey
      );
      staleKeys.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore localStorage errors
        }
      });
    },

    set(context) {
      const {
        endpoint,
        version,
        search,
        page,
        perPage,
        sortBy,
        sortOrder,
        filters,
        attrSettings,
      } = context;
      const key = stateManagerKey(endpoint, version);
      localStorage.setItem(
        key,
        JSON.stringify({
          search,
          page,
          perPage,
          sortBy,
          sortOrder,
          filters,
          attrSettings,
        })
      );
    },

    get(context) {
      const { endpoint, version } = context;
      const key = stateManagerKey(endpoint, version);
      try {
        if (localStorage.getItem(key)) {
          return JSON.parse(localStorage.getItem(key));
        } else {
          return null;
        }
      } catch {
        return null;
      }
    },
};

// Keys that are dropped from storage when filters should not be persisted.
const NON_PERSISTED_KEYS = ["filters", "search", "page"];

function omitKeys(obj = {}) {
  const next = { ...obj };
  NON_PERSISTED_KEYS.forEach((k) => delete next[k]);
  return next;
}

/**
 * State manager that keeps sort / perPage / attrSettings but never persists
 * filters or search. Filters seeded from the URL (?filters=) are read once on
 * mount and then removed from storage, so the next visit starts from defaults.
 */
const nonPersistentStateManager = {
  init: baseStateManager.init,

  set(context) {
    baseStateManager.set({
      ...context,
      filters: undefined,
      search: undefined,
      page: undefined,
    });
  },

  get(context) {
    const stored = baseStateManager.get(context);
    if (!stored) return null;

    // Consume one-time values (e.g. seeded from the URL), then clear them.
    const key = stateManagerKey(context.endpoint, context.version);
    try {
      localStorage.setItem(key, JSON.stringify(omitKeys(stored)));
    } catch {
      // Ignore localStorage errors
    }
    return stored;
  },
};

export function getListConfig({ persistFilters = true } = {}) {
  return {
    stateManager: persistFilters ? baseStateManager : nonPersistentStateManager,
    requestHandler: fetchListData,
  };
}

export default getListConfig();
